/* eslint-disable @typescript-eslint/prefer-for-of */

import {of as observableOf,  Observable } from 'rxjs';

import {catchError} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ErrorResponse, SuccessResponse } from '../response';
import { AttendeesTicketsPage, AttendeesTicketsPagedData, IAttendeeTicket, IEditUploadTicket, IPreviewPDFData, ITicketDistribution,
  ITicketPage, ITicketsDistributionData, ITicketsDistributionList, ITicketsList, ITicketsPDF, TicketPage, UploadTicket } from './tickets-model';

@Injectable()
export class TicketsService {

  constructor(private http: HttpClient) { }

  private static get URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${TicketsService.ENDPOINT}`; }
  private static readonly ENDPOINT = '/events/{eventId}/tickets';

  public getAllTickets(eventId: string): Observable<SuccessResponse<ITicketsList> | ErrorResponse> {
    return this.http
      .get<SuccessResponse<ITicketsList>>(TicketsService.URL.replace('{eventId}', eventId) + '/getall').pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public addTicketPage(model: TicketPage, eventId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
      .post<SuccessResponse<boolean>>(TicketsService.URL.replace('{eventId}', eventId), model).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public getTicketPage(eventId: string, ticketId: string): Observable<SuccessResponse<ITicketPage> | ErrorResponse> {
    return this.http
      .get<SuccessResponse<ITicketPage>>(TicketsService.URL.replace('{eventId}', eventId) + '/' + ticketId).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public updateTicketPage(eventId: string, ticketId: string, model: ITicketPage): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
      .put<SuccessResponse<boolean>>(TicketsService.URL.replace('{eventId}', eventId) + '/' + ticketId, model).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public deleteTicketPage(eventId: string, ticketId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
      .delete<SuccessResponse<boolean>>(TicketsService.URL.replace('{eventId}', eventId) + '/' + ticketId).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public searchUsersForTickesDistribution(model: ITicketDistribution, eventId: string): Observable<SuccessResponse<ITicketsDistributionList> | ErrorResponse> {
    return this.http
      .post<SuccessResponse<ITicketsDistributionList>>(TicketsService.URL.replace('{eventId}', eventId) + '/distribution', model).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public getAttendeesTickets(eventId: string, page: AttendeesTicketsPage): Observable<SuccessResponse<AttendeesTicketsPagedData<IAttendeeTicket>> | ErrorResponse> {
    return this.http
      .post<SuccessResponse<AttendeesTicketsPagedData<IAttendeeTicket>>>(TicketsService.URL.replace('{eventId}', eventId) + '/attendees', page).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public distribution(eventId: string, model: ITicketsDistributionData): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
      .put<SuccessResponse<boolean>>(TicketsService.URL.replace('{eventId}', eventId) + '/distributionAction', model).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public giveTicketForAll(eventId: string, ticketId: string, model: ITicketDistribution): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
      .put<SuccessResponse<boolean>>(TicketsService.URL.replace('{eventId}', eventId) + '/' + ticketId + '/giveTicketForAll', model).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public removeTicketFromAll(eventId: string, ticketId: string, model: ITicketDistribution): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
      .put<SuccessResponse<boolean>>(TicketsService.URL.replace('{eventId}', eventId) + '/' + ticketId + '/universalRemoveTickets', model).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public getTicketPDFPreview(eventId: string, data): Observable<SuccessResponse<IPreviewPDFData> | ErrorResponse> {
    return this.http
      .post<SuccessResponse<IPreviewPDFData>>(TicketsService.URL.replace('{eventId}', eventId) + '/previewTicket', data).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public uploadTicket(eventId: string, model: UploadTicket): Observable<SuccessResponse<boolean> | ErrorResponse> {

    const formData: FormData = new FormData();

    const limitedTicket = JSON.stringify(model.limitedTicket);
    const ticketDistribution = JSON.stringify(model.ticketDistribution);
    const numberOfTickets = JSON.stringify(model.numberOfTickets);
    const split = JSON.stringify(model.split);
    const multipleTicket = JSON.stringify(model.multipleTicket);
    formData.append('limitedTicket', limitedTicket);
    formData.append('name', model.name);
    formData.append('ticketDistribution', ticketDistribution);
    formData.append('numberOfTickets', numberOfTickets);
    formData.append('split', split);
    formData.append('multipleTicket', multipleTicket);

    // Add files
    for (let i = 0; i < model.files.length; i++) {
      formData.append('files', model.files[i]);
    }

    return this.http
      .post<SuccessResponse<boolean>>(TicketsService.URL.replace('{eventId}', eventId) + '/uploadTicket', formData).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public updateUploadedTicket(eventId: string, model: IEditUploadTicket): Observable<SuccessResponse<boolean> | ErrorResponse> {

    const formData: FormData = new FormData();
    const numberOfTickets = JSON.stringify(model.numberOfTickets);
    const removingFiles = JSON.stringify(model.removingFiles);
    const deleteAll = JSON.stringify(model.deleteAll);
    formData.append('id', model.id);
    formData.append('name', model.name);
    formData.append('numberOfTickets', numberOfTickets);
    formData.append('deleteAll', deleteAll);
    formData.append('removingFiles', removingFiles);

    for (let i = 0; i < model.newFiles.length; i++) {
      formData.append('newFiles', model.newFiles[i]);
    }

    if (!model.newFiles.length) {
      formData.append('newFiles', JSON.stringify(model.newFiles));
    }

    return this.http
      .put<SuccessResponse<boolean>>(TicketsService.URL.replace('{eventId}', eventId) + '/updateUploadedTicket', formData).pipe(
      catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public getTicketsPDFUrl(eventId: string, invitationId: string): Observable<SuccessResponse<ITicketsPDF> | ErrorResponse> {
    return this.http
    .get<SuccessResponse<ITicketsPDF>>(TicketsService.URL.replace('{eventId}', eventId) + '/ticketsUrl/' + invitationId).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

}
